import { useState, useEffect, useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, Phone, ChevronRight, CalendarDays, Sparkles } from "lucide-react";
import { useNavigate } from "react-router-dom";

// Hero image options - Your actual builds
const HERO_OPTIONS = {
  original: {
    url: "https://customer-assets.emergentagent.com/job_auto-paint-brisbane/artifacts/4s7jjnjm_IMG_3046.jpeg",
    label: "Original (NSX + R32)"
  },
  gc8: {
    url: "https://customer-assets-jai6qajn.emergentagent.net/job_1406887e-27f1-4dc9-a01d-472f5d3042b6/artifacts/3occ3nlq_IMG_0448.jpeg",
    label: "Black GC8 WRX"
  },
  blobeye: {
    url: "https://customer-assets-jai6qajn.emergentagent.net/job_1406887e-27f1-4dc9-a01d-472f5d3042b6/artifacts/dlbr9rqm_IMG_0450.jpeg",
    label: "Grey Blobeye WRX"
  },
  rx7: {
    url: "https://customer-assets-jai6qajn.emergentagent.net/job_1406887e-27f1-4dc9-a01d-472f5d3042b6/artifacts/tr6mfqet_IMG_0458.jpeg",
    label: "Blue RX-7 FD"
  },
  r34: {
    url: "https://customer-assets-jai6qajn.emergentagent.net/job_1406887e-27f1-4dc9-a01d-472f5d3042b6/artifacts/jqyyjfzm_IMG_8372.jpeg",
    label: "White R34 Skyline"
  }
};

export const HeroSectionAnimated = ({ heroOption = "gc8" }) => {
  const navigate = useNavigate();
  const containerRef = useRef(null);
  const [currentHero, setCurrentHero] = useState(heroOption);
  
  // Scroll-based animations
  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start start", "end start"]
  });
  
  // Parallax effect - image moves slower than scroll
  const imageY = useTransform(scrollYProgress, [0, 1], ["0%", "30%"]);
  const imageScale = useTransform(scrollYProgress, [0, 1], [1, 1.1]);
  const textY = useTransform(scrollYProgress, [0, 1], ["0%", "50%"]);
  const opacity = useTransform(scrollYProgress, [0, 0.5], [1, 0]);

  const scrollToQuote = () => {
    document.querySelector("#quote")?.scrollIntoView({ behavior: "smooth" });
  };

  // Text reveal animation variants
  const textReveal = {
    hidden: { 
      opacity: 0, 
      y: 100,
      clipPath: "inset(100% 0% 0% 0%)"
    },
    visible: (delay) => ({
      opacity: 1,
      y: 0,
      clipPath: "inset(0% 0% 0% 0%)",
      transition: {
        duration: 0.8,
        delay: delay,
        ease: [0.25, 0.4, 0.25, 1]
      }
    })
  };

  const fadeUp = {
    hidden: { opacity: 0, y: 30 },
    visible: (delay) => ({
      opacity: 1,
      y: 0,
      transition: { duration: 0.6, delay, ease: "easeOut" }
    })
  };

  return (
    <section 
      ref={containerRef}
      id="top" 
      data-testid="hero-section-animated" 
      className="relative min-h-screen flex items-center overflow-hidden"
    >
      {/* Animated Background Image with Parallax */}
      <motion.div 
        className="absolute inset-0 z-0"
        style={{ y: imageY, scale: imageScale }}
      >
        <img
          src={HERO_OPTIONS[currentHero].url}
          alt="Velocity Paint and Panel"
          className="w-full h-full object-cover"
        />
        {/* Dark gradient overlay for text readability */}
        <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/60 to-black/40" />
        {/* Grain texture overlay for premium feel */}
        <div className="absolute inset-0 opacity-30" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)'/%3E%3C/svg%3E")`,
        }} />
      </motion.div>

      {/* Hero Toggle - Demo Only */}
      <div className="absolute top-24 right-6 z-50 flex flex-col gap-2">
        <span className="text-xs text-zinc-500 uppercase tracking-wider mb-1">Hero Option:</span>
        {Object.entries(HERO_OPTIONS).map(([key, { label }]) => (
          <button
            key={key}
            onClick={() => setCurrentHero(key)}
            className={`px-3 py-1.5 text-xs uppercase tracking-wider transition-all cursor-pointer ${
              currentHero === key 
                ? "bg-[#3b82f6] text-white border-[#3b82f6]" 
                : "bg-black/50 text-zinc-400 border-zinc-700 hover:border-zinc-500"
            } border`}
          >
            {label}
          </button>
        ))}
      </div>

      {/* Watermark with parallax */}
      <motion.div 
        className="absolute inset-0 flex items-center justify-center pointer-events-none z-[1] overflow-hidden"
        style={{ y: textY, opacity }}
      >
        <span className="text-[20vw] font-heading font-black text-white/[0.03] uppercase tracking-tighter whitespace-nowrap">
          VELOCITY
        </span>
      </motion.div>

      {/* Main Content */}
      <motion.div 
        className="relative z-10 max-w-[1400px] mx-auto px-6 md:px-12 w-full pt-32 pb-24"
        style={{ y: textY, opacity }}
      >
        <div className="max-w-3xl">
          {/* Location tag */}
          <motion.div
            variants={fadeUp}
            initial="hidden"
            animate="visible"
            custom={0.2}
            className="flex items-center gap-3 mb-8"
          >
            <motion.div 
              className="w-12 h-[1px] bg-[#3b82f6]"
              initial={{ scaleX: 0 }}
              animate={{ scaleX: 1 }}
              transition={{ duration: 0.8, delay: 0.3 }}
              style={{ originX: 0 }}
            />
            <span data-testid="hero-tagline" className="text-sm tracking-[0.3em] uppercase text-zinc-400 font-body font-medium">
              101 Norman St, Woolloongabba, Brisbane
            </span>
          </motion.div>

          {/* Main Heading with dramatic reveal */}
          <div className="overflow-hidden mb-8">
            <motion.h1
              data-testid="hero-heading"
              className="font-heading text-5xl sm:text-6xl lg:text-8xl uppercase leading-[0.9] tracking-tight text-white"
            >
              <motion.span 
                className="block"
                variants={textReveal}
                initial="hidden"
                animate="visible"
                custom={0.4}
              >
                Precision
              </motion.span>
              <motion.span 
                className="block"
                variants={textReveal}
                initial="hidden"
                animate="visible"
                custom={0.5}
              >
                Automotive
              </motion.span>
              <motion.span 
                className="block text-[#3b82f6]"
                variants={textReveal}
                initial="hidden"
                animate="visible"
                custom={0.6}
              >
                Refinishing
              </motion.span>
            </motion.h1>
          </div>

          {/* Subtext */}
          <motion.p
            variants={fadeUp}
            initial="hidden"
            animate="visible"
            custom={0.8}
            data-testid="hero-subtext"
            className="text-base md:text-lg text-zinc-400 font-body font-light max-w-xl mb-12 leading-relaxed"
          >
            JDM specialists delivering full resprays, custom paint jobs, and flawless repairs.
            No insurance work, just pure craftsmanship.
          </motion.p>

          {/* CTA Buttons with stagger */}
          <motion.div
            variants={fadeUp}
            initial="hidden"
            animate="visible"
            custom={1}
            className="flex flex-col sm:flex-row gap-4"
          >
            <motion.button
              onClick={scrollToQuote}
              data-testid="hero-quote-btn"
              className="group relative flex items-center justify-center gap-2 bg-white text-black font-heading text-lg tracking-[0.15em] uppercase px-10 py-4 overflow-hidden cursor-pointer border-none"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              {/* Hover sweep effect */}
              <motion.div 
                className="absolute inset-0 bg-[#3b82f6]"
                initial={{ x: "-100%" }}
                whileHover={{ x: 0 }}
                transition={{ duration: 0.3 }}
              />
              <span className="relative z-10 group-hover:text-white transition-colors">Get a Quote</span>
              <ChevronRight size={18} className="relative z-10 group-hover:text-white transition-colors" />
            </motion.button>
            
            <motion.button
              onClick={() => navigate("/book")}
              data-testid="hero-book-btn"
              className="flex items-center justify-center gap-2 border border-[#3b82f6]/40 text-[#3b82f6] font-heading text-lg tracking-[0.15em] uppercase px-10 py-4 hover:bg-[#3b82f6]/10 transition-colors cursor-pointer bg-transparent"
              whileHover={{ scale: 1.02, borderColor: "rgba(59, 130, 246, 0.8)" }}
              whileTap={{ scale: 0.98 }}
            >
              <CalendarDays size={16} />
              Book Appointment
            </motion.button>
            
            <motion.a
              href="sms:0450645926"
              data-testid="hero-call-btn"
              className="flex items-center justify-center gap-2 border border-white/20 text-white font-heading text-lg tracking-[0.15em] uppercase px-10 py-4 hover:bg-white/5 transition-colors no-underline"
              whileHover={{ scale: 1.02, borderColor: "rgba(255, 255, 255, 0.4)" }}
              whileTap={{ scale: 0.98 }}
            >
              <Phone size={16} />
              0450 645 926
            </motion.a>
          </motion.div>
        </div>
      </motion.div>

      {/* Scroll indicator */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.5 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 flex flex-col items-center gap-2"
      >
        <span className="text-xs tracking-[0.3em] uppercase text-zinc-500 font-body">Scroll</span>
        <motion.div 
          animate={{ y: [0, 8, 0] }} 
          transition={{ repeat: Infinity, duration: 1.5, ease: "easeInOut" }}
        >
          <ArrowDown size={16} className="text-zinc-500" />
        </motion.div>
      </motion.div>
    </section>
  );
};

export default HeroSectionAnimated;
